'use client';

/**
 * **레이아웃에 한 번 두는 Provider** — 화면 맥락(uid · WS · 경로)을 SDK 에 넣고, 경로가 바뀔 때
 * `page_viewed` 를, 로그인이 확인될 때 `user_identified` 를 남기고, 언로드에 큐를 흘려보낸다.
 *
 * 광고·외부 링크 유입(`?ad=` · 클릭 id)과 영업 메일 유입(`?cm=`)도 여기서 받아 적는다(`ad-landing` · `outreach-landing`) — 어느 화면으로 들어와도 잡힌다.
 * 영업 메일의 admin 클릭 보고·30일 표식(`OutreachClickTracker`)도 여기서 그린다.
 * 마케팅 사이트의 체류·스크롤(`page_engaged`)도 경로가 바뀔 때 여기서 잰다(`page-engagement`).
 * 랜딩 A/B 판(쿠키 `cm_lp`)도 여기서 맥락에 싣는다(`landing-experiment`) — 인증을 기다리지 않고 경로가 바뀔 때마다 다시 읽는다.
 *
 * 레이아웃 넷(main · service · auth-basic-header · auth-basic-page)이 각자 이 컴포넌트를 두어도 SDK 상태는 모듈 하나라
 * 같은 경로에 `page_viewed` 가 두 번 나가지 않는다(`trackPageView` 가 경로로 중복을 거른다).
 * 인증 로딩 중에는 아무것도 보내지 않는다 — uid 가 확정되기 전 게스트로 찍히면 세션이 한 번 더 회전한다.
 *
 * 전 화면에 마운트되는 자리라 「새 버전이 배포되었습니다」 다이얼로그도 여기서 그린다
 * (수집 응답의 `x-app-version` 을 견주는 `version-skew` 가 깨운다).
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import { OutreachClickTracker } from 'src/components/cmui/outreach-click-tracker';
import { AppVersionRefreshDialog } from 'src/components/cmui/app-version-refresh-dialog';

import { useAuthContext } from 'src/auth/hooks';

import { captureAdLanding } from './ad-landing';
import { startPageEngagement } from './page-engagement';
import { captureOutreachLanding } from './outreach-landing';
import { applyLandingExperiment } from './landing-experiment';
import { identify, trackPageView, setAnalyticsContext, flushAnalyticsOnUnload } from './client';

// ----------------------------------------------------------------------

export function AnalyticsProvider() {
  const pathname = usePathname();
  const { user, loading } = useAuthContext();
  const uid = typeof user?.uid === 'string' && user.uid ? user.uid : null;
  const workspaceId = typeof user?.lastWorkspace === 'string' && user.lastWorkspace ? user.lastWorkspace : null;

  // 한 효과에서 맥락 → 화면 진입 → 신원 순서로 — `page_viewed` 가 WS 를, `user_identified` 가 지금 path 를 갖게.
  // 둘 다 자기 중복(같은 경로 · 같은 uid)을 거르므로 의존성이 겹쳐도 두 번 나가지 않는다.
  useEffect(() => {
    applyLandingExperiment();
    if (loading) return;
    setAnalyticsContext({ workspaceId });
    // 체류 측정이 먼저 — 직전 화면의 마지막 줄(route_change)이 바뀌기 전 경로로 찍혀야 한다
    if (pathname) startPageEngagement(pathname);
    if (pathname) trackPageView(pathname);
    captureAdLanding();
    captureOutreachLanding();
    identify(uid);
  }, [pathname, uid, workspaceId, loading]);

  useEffect(() => {
    const onHide = () => flushAnalyticsOnUnload();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  return (
    <>
      <OutreachClickTracker />
      <AppVersionRefreshDialog />
    </>
  );
}
